import { useState, useEffect } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import './FileUpload.css';

// Sortable item component
function SortableItem({ id, file, index, onRemove }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const [preview, setPreview] = useState(null);

  // Generate preview on mount
  useEffect(() => {
    const reader = new FileReader();
    reader.onloadend = () => setPreview(reader.result);
    reader.readAsDataURL(file);
  }, [file]);

  return (
    <div ref={setNodeRef} style={style} className="file-item">
      <div className="drag-handle" {...attributes} {...listeners}>
        ⋮⋮
      </div>
      <div className="file-preview">
        {preview && <img src={preview} alt={`Page ${index + 1}`} />}
      </div>
      <div className="file-info">
        <span className="file-name">page-{String(index + 1).padStart(3, '0')}.webp</span>
        <span className="file-original">{file.name}</span>
      </div>
      <button 
        type="button" 
        className="btn-remove" 
        onClick={() => onRemove(id)}
      >
        ✕
      </button>
    </div>
  );
}

function FileUpload({ files, onChange }) {
  const [items, setItems] = useState([]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // When files change from parent, create items with IDs
  useEffect(() => {
    const newItems = files.map((file, idx) => ({
      id: `${file.name}-${idx}`,
      file,
    }));
    setItems(newItems);
  }, [files]);

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      setItems((items) => {
        const oldIndex = items.findIndex((i) => i.id === active.id);
        const newIndex = items.findIndex((i) => i.id === over.id);
        const newItems = arrayMove(items, oldIndex, newIndex);
        
        // Update parent with reordered files
        onChange(newItems.map(item => item.file));
        
        return newItems;
      });
    }
  };

  const handleRemove = (id) => {
    const newItems = items.filter(item => item.id !== id);
    setItems(newItems);
    onChange(newItems.map(item => item.file));
  };

  if (items.length === 0) {
    return <p className="no-files">Aucun fichier sélectionné</p>;
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={items.map(i => i.id)}
        strategy={verticalListSortingStrategy}
      >
        <div className="file-list">
          {items.map((item, index) => (
            <SortableItem
              key={item.id}
              id={item.id}
              file={item.file}
              index={index}
              onRemove={handleRemove}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}

export default FileUpload;